// ---------------------------------------------------------------------------
// YouAreYou (uru) — Reality Check Page
//
// Public utility + marketing page at /reality-check.
// "Are they really who they say they are?"
//
// Visitors enter a phone number, email, or name + city to check whether
// that person is a YouAreYou Verified member — before meeting, renting,
// buying, or hiring.
//
// Search results:
//   verified (unique)   → green badge + "Connect with them" → /v/{code}
//   verified (multiple) → amber badge + "Ask them to share their link"
//   not_found           → gray badge + "Ask them to verify" invite copy
//   unavailable         → graceful degraded state (shown until backend ready)
//
// Backend dependency: POST /api/uru/reality-check (ISSUE-020)
// Connection code:    Returned on match_count=1 per ISSUE-021
// Rate limiting:      Handled at infra level per ISSUE-022
//
// Design follows LandingPage conventions: Public Sans, primary blue (#0052FF),
// Material Symbols Outlined icons, Tailwind v4 design tokens.
// ---------------------------------------------------------------------------

import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import BrandName from "@/components/ui/BrandName";
import BrandMark from "@/components/ui/BrandMark";
import { apiClient } from "@/api/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SearchType = "phone" | "email" | "name_location";

interface RealityCheckRequest {
  search_type: SearchType;
  phone?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  state?: string;
}

interface RealityCheckData {
  found: boolean;
  verified: boolean;
  match_count: number;
  search_type: SearchType;
  connection_code?: string;
}

type ResultStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "verified"; data: RealityCheckData }
  | { kind: "not_found" }
  | { kind: "unavailable" };

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/** Normalize a phone input to E.164. Handles US 10-digit and 11-digit formats. */
function normalizePhone(input: string): string {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+${digits}`;
}

function isValidPhone(input: string): boolean {
  return input.replace(/\D/g, "").length >= 10;
}

function isValidEmail(input: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());
}

const INVITE_MESSAGE =
  "Hey, I'd like to verify your identity before we meet — it's free and takes about 2 minutes. You can do it here: https://youareyou.io/register";

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark className="h-8 w-8" />
          <span className="text-lg font-bold">
            <BrandName />
          </span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <Link
            to="/how-it-works"
            className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            How it works
          </Link>
          <Link
            to="/reality-check"
            className="text-sm font-medium text-primary-500"
          >
            Reality Check
          </Link>
          <Link
            to="/security"
            className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            Security
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm font-semibold text-text-secondary transition-colors hover:text-text-primary"
          >
            Sign In
          </Link>
          <Link
            to="/register"
            className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Get Started
          </Link>
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function Footer() {
  return (
    <footer className="border-t border-gray-200 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <span className="text-sm font-bold">
              <BrandName />
            </span>
          </Link>

          <div className="flex items-center gap-4 text-xs text-text-muted">
            <Link to="/privacy" className="transition-colors hover:text-text-secondary">
              Privacy Policy
            </Link>
            <Link to="/terms" className="transition-colors hover:text-text-secondary">
              Terms of Service
            </Link>
            <Link to="/support" className="transition-colors hover:text-text-secondary">
              Support
            </Link>
          </div>

          <p className="text-[11px] text-text-muted">
            &copy; {new Date().getFullYear()} UrU.io. All rights reserved.
          </p>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 border-t border-gray-100 pt-5">
          <span className="text-xs text-text-muted">Powered by</span>
          <span className="text-xs font-bold text-text-secondary">Mojo Verify</span>
          <Icon name="verified_user" filled className="text-sm text-primary-500" />
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// CopyButton — copy-to-clipboard with feedback
// ---------------------------------------------------------------------------

function CopyButton({ text, label = "Copy message" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-text-secondary transition-colors hover:border-gray-400 hover:text-text-primary"
    >
      <Icon name={copied ? "check_circle" : "content_copy"} className="text-sm" />
      {copied ? "Copied!" : label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ResultCard — shows the outcome of a reality check search
// ---------------------------------------------------------------------------

function ResultCard({
  result,
  searchType,
  citySearched,
}: {
  result: Exclude<ResultStatus, { kind: "idle" } | { kind: "loading" }>;
  searchType: SearchType;
  citySearched?: string;
}) {
  const searchLabel =
    searchType === "phone"
      ? "phone number"
      : searchType === "email"
        ? "email address"
        : "person";

  if (result.kind === "verified") {
    const { data } = result;
    const isMultiple = data.match_count > 1;

    if (isMultiple) {
      return (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <Icon name="group" filled className="text-xl text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-800">
                {data.match_count} Verified Matches Found
              </p>
              <p className="mt-1 text-sm text-amber-700">
                We found {data.match_count} YouAreYou Verified people matching this
                {citySearched ? ` name in ${citySearched}` : " name"}.
                For a definitive answer, ask them to share their personal link.
              </p>
            </div>
          </div>

          <div className="mt-4 border-t border-amber-200 pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
              Ask them to share their YouAreYou link:
            </p>
            <div className="rounded-lg border border-amber-200 bg-white p-3 text-xs text-text-secondary">
              {INVITE_MESSAGE}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <CopyButton text={INVITE_MESSAGE} label="Copy invite" />
            </div>
          </div>
        </div>
      );
    }

    // Unique match
    return (
      <div className="mt-6 rounded-2xl border border-green-200 bg-green-50 p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100">
            <Icon name="verified" filled className="text-xl text-green-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-green-800">
                YouAreYou Verified
              </p>
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700 ring-1 ring-green-200">
                <Icon name="check" className="text-xs" />
                Confirmed
              </span>
            </div>
            <p className="mt-1 text-sm text-green-700">
              This {searchLabel} is linked to a YouAreYou Verified person. Their
              Government ID and liveness have been confirmed — they are who they say they are.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-t border-green-200 pt-4">
          <div className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-text-secondary ring-1 ring-green-200">
            <Icon name="badge" filled className="text-sm text-green-600" />
            Government ID
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-text-secondary ring-1 ring-green-200">
            <Icon name="face" filled className="text-sm text-green-600" />
            Liveness Check
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-text-secondary ring-1 ring-green-200">
            <Icon name="phone_iphone" filled className="text-sm text-green-600" />
            Phone Verified
          </div>
        </div>

        <div className="mt-4">
          {data.connection_code ? (
            <Link
              to={`/v/${data.connection_code}`}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-primary-600"
            >
              <Icon name="link" className="text-base" />
              Connect with them
            </Link>
          ) : (
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-primary-600"
            >
              <Icon name="link" className="text-base" />
              Connect with them
            </Link>
          )}
          <p className="mt-2 text-xs text-text-muted">
            You'll be asked to sign in or create a free account to send a connection request.
          </p>
        </div>
      </div>
    );
  }

  if (result.kind === "not_found") {
    return (
      <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200">
            <Icon name="help_outline" className="text-xl text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-text-primary">Not Yet Verified</p>
            <p className="mt-1 text-sm text-text-secondary">
              We don't have a verified record for this {searchLabel}. They may not
              have joined YouAreYou yet, or may have used different contact details.
            </p>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-200 pt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Send them this invite:
          </p>
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs text-text-secondary">
            {INVITE_MESSAGE}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <CopyButton text={INVITE_MESSAGE} label="Copy invite" />
            <Link
              to="/register"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <Icon name="verified_user" className="text-sm" />
              Get verified yourself
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // unavailable
  return (
    <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200">
          <Icon name="schedule" className="text-xl text-gray-500" />
        </div>
        <div>
          <p className="text-sm font-bold text-text-primary">Live Search Coming Soon</p>
          <p className="mt-1 text-sm text-text-secondary">
            The live search feature is being set up. In the meantime, ask them
            to share their YouAreYou link directly — or send them an invite to get verified.
          </p>
        </div>
      </div>

      <div className="mt-4 border-t border-gray-200 pt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          Send them this invite:
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs text-text-secondary">
          {INVITE_MESSAGE}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <CopyButton text={INVITE_MESSAGE} label="Copy invite" />
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-text-secondary transition-colors hover:border-gray-400"
          >
            Learn about YouAreYou
          </Link>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SearchPanel — main utility section
// ---------------------------------------------------------------------------

const SEARCH_TABS: { type: SearchType; label: string; icon: string }[] = [
  { type: "phone", label: "Phone Number", icon: "phone" },
  { type: "email", label: "Email Address", icon: "mail" },
  { type: "name_location", label: "Name + City", icon: "person_search" },
];

function SearchPanel() {
  const [activeTab, setActiveTab] = useState<SearchType>("phone");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [result, setResult] = useState<ResultStatus>({ kind: "idle" });
  const [validationError, setValidationError] = useState("");

  function validate(): string {
    if (activeTab === "phone") {
      if (!phone.trim()) return "Enter a phone number.";
      if (!isValidPhone(phone)) return "Enter a valid phone number (at least 10 digits).";
    }
    if (activeTab === "email") {
      if (!email.trim()) return "Enter an email address.";
      if (!isValidEmail(email)) return "Enter a valid email address.";
    }
    if (activeTab === "name_location") {
      if (!firstName.trim()) return "Enter a first name.";
      if (!lastName.trim()) return "Enter a last name.";
      if (!city.trim()) return "Enter a city.";
    }
    return "";
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validate();
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError("");
    setResult({ kind: "loading" });

    const body: RealityCheckRequest = { search_type: activeTab };
    if (activeTab === "phone") body.phone = normalizePhone(phone);
    if (activeTab === "email") body.email = email.trim().toLowerCase();
    if (activeTab === "name_location") {
      body.first_name = firstName.trim();
      body.last_name = lastName.trim();
      body.city = city.trim();
      if (state.trim()) body.state = state.trim().toUpperCase();
    }

    try {
      const data = await apiClient.post<RealityCheckData>(
        "/api/uru/reality-check",
        body,
        { noAuth: true },
      );

      if (data.found && data.verified) {
        setResult({ kind: "verified", data });
      } else {
        setResult({ kind: "not_found" });
      }
    } catch (err: unknown) {
      // 404 = endpoint not yet deployed; treat as unavailable
      if (
        err &&
        typeof err === "object" &&
        "statusCode" in err &&
        (err as { statusCode: number }).statusCode === 404
      ) {
        setResult({ kind: "unavailable" });
      } else {
        setResult({ kind: "unavailable" });
      }
    }
  }

  function handleTabChange(tab: SearchType) {
    setActiveTab(tab);
    setResult({ kind: "idle" });
    setValidationError("");
  }

  const isLoading = result.kind === "loading";

  return (
    <section className="bg-white py-8 sm:py-10">
      <div className="mx-auto max-w-xl px-4 sm:px-6">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-md">

          {/* Tab strip */}
          <div className="flex overflow-hidden rounded-t-2xl border-b border-gray-200 bg-gray-50">
            {SEARCH_TABS.map((tab) => (
              <button
                key={tab.type}
                onClick={() => handleTabChange(tab.type)}
                className={`flex flex-1 flex-col items-center gap-1 py-3 text-center transition-colors ${
                  activeTab === tab.type
                    ? "border-b-2 border-primary-500 bg-white text-primary-600"
                    : "text-text-muted hover:text-text-secondary"
                }`}
              >
                <Icon
                  name={tab.icon}
                  filled={activeTab === tab.type}
                  className={`text-xl ${activeTab === tab.type ? "text-primary-500" : "text-text-muted"}`}
                />
                <span className="text-[11px] font-semibold leading-none">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5">
            {activeTab === "phone" && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-text-secondary">
                  Phone number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  autoFocus
                />
              </div>
            )}

            {activeTab === "email" && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-text-secondary">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="someone@example.com"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  autoFocus
                />
              </div>
            )}

            {activeTab === "name_location" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-text-secondary">
                      First name
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-text-secondary">
                      Last name
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Smith"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="mb-1.5 block text-xs font-semibold text-text-secondary">
                      City
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Austin"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-text-secondary">
                      State
                      <span className="ml-1 font-normal text-text-muted">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder="TX"
                      maxLength={2}
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>
              </div>
            )}

            {validationError && (
              <p className="mt-2 text-xs text-red-600">{validationError}</p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {isLoading ? (
                <>
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Checking...
                </>
              ) : (
                <>
                  <Icon name="search" className="text-base" />
                  Check Now
                </>
              )}
            </button>

            {/* Privacy note */}
            <p className="mt-3 text-center text-[11px] text-text-muted">
              <Icon name="lock" className="mr-0.5 text-xs" />
              Your search is private and not stored.
            </p>
          </form>

          {/* Real result card — shown after search */}
          {result.kind !== "idle" && result.kind !== "loading" && (
            <div className="border-t border-gray-100 px-5 pb-5">
              <ResultCard
                result={result}
                searchType={activeTab}
                citySearched={city || undefined}
              />
            </div>
          )}
        </div>
      </div>

      {/* Recent search example — own wider wrapper, outside the max-w-xl constraint */}
      {result.kind === "idle" && (
        <div className="mx-auto mt-4 max-w-4xl px-4 sm:px-6">
          {/* Header */}
          <div className="mb-2 px-1">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-text-muted">
              <Icon name="history" className="text-sm" />
              Recent Search
            </span>
          </div>

          {/* Row */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-gray-50">
          <div className="flex min-w-max items-center gap-3 px-4 py-3">

            {/* Phone number */}
            <div className="flex shrink-0 items-center gap-1.5">
              <Icon name="phone" filled className="text-sm text-text-muted" />
              <span className="font-mono text-sm font-semibold tracking-wider text-text-primary">
                510 283 ****
              </span>
            </div>

            <div className="h-4 w-px shrink-0 bg-gray-300" />

            {/* Verified badge */}
            <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-200">
              <Icon name="verified" filled className="text-sm text-green-600" />
              <span className="text-xs font-bold text-green-700">YouAreYou Verified</span>
            </div>

            <div className="h-4 w-px shrink-0 bg-gray-300" />

            {/* Verification factors */}
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-xs text-text-muted">Phone linked &amp; verified with</span>
              <span className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-text-secondary ring-1 ring-gray-200">
                <Icon name="badge" filled className="text-xs text-green-600" />
                Government ID
              </span>
              <span className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-text-secondary ring-1 ring-gray-200">
                <Icon name="face" filled className="text-xs text-green-600" />
                Liveness Check
              </span>
            </div>

            {/* Trusted label — pinned right */}
            <div className="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5">
              <Icon name="shield" filled className="text-sm text-white" />
              <span className="text-xs font-bold text-white">Trusted</span>
            </div>

          </div>
          </div>
        </div>
      )}

    </section>
  );
}

// ---------------------------------------------------------------------------
// TrustDiagramSection — visual depiction of YouAreYou as the trusted
// intermediary: data flows IN from the person, only the verified result
// flows OUT to the searcher. The raw documents never cross.
// ---------------------------------------------------------------------------

const PERSON_DATA_ITEMS = [
  { icon: "badge",         label: "Government ID" },
  { icon: "face",          label: "Face Scan" },
  { icon: "phone_iphone",  label: "Phone Number" },
  { icon: "cake",          label: "Date of Birth" },
];

const BLOCKED_ITEMS = [
  "Gov ID document",
  "Face photos",
  "Phone number",
  "Personal data",
];

function TrustDiagramSection() {
  return (
    <section className="bg-background-dark py-14 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">

        {/* Header */}
        <div className="mb-10 text-center">
          <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
            Know they're real. Keep your data private.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-400">
            When someone searches for you, they see a trusted result — never your documents.
            YouAreYou holds the proof. Only the badge passes through.
          </p>
        </div>

        {/* Diagram: 3 nodes + 2 connectors */}
        <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-stretch md:gap-0">

          {/* ── Node 1: The Person ────────────────────────────── */}
          <div className="flex flex-1 flex-col rounded-2xl border border-slate-700/60 bg-slate-800/50 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-700">
              <Icon name="person" filled className="text-2xl text-slate-300" />
            </div>
            <p className="mt-3 text-sm font-bold text-white">The Person</p>
            <p className="mt-0.5 text-xs text-slate-400">Verifies once, privately</p>

            <div className="mt-5 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Submits to YouAreYou
              </p>
              {PERSON_DATA_ITEMS.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2.5 rounded-lg border border-slate-700/50 bg-slate-700/30 px-3 py-2"
                >
                  <Icon name={item.icon} filled className="text-sm text-slate-400" />
                  <span className="text-xs font-medium text-slate-300">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Connector 1: data flows in ───────────────────── */}
          {/* Desktop: horizontal. Mobile: vertical. */}
          <div className="flex items-center justify-center md:flex-col md:px-1">
            {/* Mobile arrow (down) */}
            <div className="flex flex-col items-center gap-1 md:hidden">
              <div className="h-4 w-px bg-primary-500/40" />
              <Icon name="arrow_downward" className="text-xl text-primary-400" />
              <span className="rounded-full bg-primary-900/60 px-2 py-0.5 text-[10px] font-semibold text-primary-400">
                Encrypted
              </span>
              <div className="h-4 w-px bg-primary-500/40" />
            </div>
            {/* Desktop arrow (right) */}
            <div className="hidden flex-col items-center gap-1 md:flex">
              <span className="mb-1 rounded-full bg-primary-900/60 px-2 py-0.5 text-[10px] font-semibold text-primary-400">
                Encrypted
              </span>
              <Icon name="arrow_forward" className="text-xl text-primary-400" />
            </div>
          </div>

          {/* ── Node 2: YouAreYou — the shield ───────────────── */}
          <div className="relative flex flex-1 flex-col items-center rounded-2xl border-2 border-primary-500/50 bg-gradient-to-b from-primary-600/20 to-primary-950/40 p-6 shadow-xl shadow-primary-900/20">
            {/* Subtle glow */}
            <div className="absolute inset-0 -z-10 rounded-2xl bg-primary-500/8 blur-2xl" />

            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 shadow-lg shadow-primary-500/40 ring-4 ring-primary-500/20">
              <Icon name="shield" filled className="text-3xl text-white" />
            </div>
            <p className="mt-3 text-sm font-bold text-white">
              <BrandName />
            </p>
            <p className="mt-0.5 text-center text-xs text-primary-300">
              Verifies. Protects. Certifies.
            </p>

            <div className="mt-5 w-full space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-primary-400/60">
                Inside the vault
              </p>
              {[
                { icon: "lock",           label: "Encrypted at rest",    color: "text-primary-400" },
                { icon: "verified_user",  label: "Identity confirmed",   color: "text-green-400"   },
                { icon: "visibility_off", label: "Data never passed on", color: "text-primary-400" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2.5 rounded-lg border border-primary-500/20 bg-primary-500/10 px-3 py-2"
                >
                  <Icon name={item.icon} filled className={`text-sm ${item.color}`} />
                  <span className="text-xs font-medium text-primary-200">{item.label}</span>
                </div>
              ))}
            </div>

            {/* "You don't need to see the ID" callout */}
            <div className="mt-5 w-full rounded-xl border border-primary-400/20 bg-primary-400/10 px-3 py-2.5 text-center">
              <p className="text-xs font-semibold text-primary-200">
                You don't need to see the ID.
              </p>
              <p className="mt-0.5 text-[11px] text-primary-300/80">
                We've already checked it.
              </p>
            </div>
          </div>

          {/* ── Connector 2: only verified status passes ─────── */}
          <div className="flex items-center justify-center md:flex-col md:px-1">
            {/* Mobile arrow (down) */}
            <div className="flex flex-col items-center gap-1 md:hidden">
              <div className="h-4 w-px bg-green-500/40" />
              <Icon name="arrow_downward" className="text-xl text-green-500" />
              <span className="rounded-full bg-green-900/60 px-2 py-0.5 text-[10px] font-semibold text-green-400">
                Result only
              </span>
              <div className="h-4 w-px bg-green-500/40" />
            </div>
            {/* Desktop arrow (right) */}
            <div className="hidden flex-col items-center gap-1 md:flex">
              <span className="mb-1 rounded-full bg-green-900/60 px-2 py-0.5 text-[10px] font-semibold text-green-400">
                Result only
              </span>
              <Icon name="arrow_forward" className="text-xl text-green-500" />
            </div>
          </div>

          {/* ── Node 3: What you see ──────────────────────────── */}
          <div className="flex flex-1 flex-col rounded-2xl border border-green-700/40 bg-green-950/30 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-900/60 ring-2 ring-green-600/40">
              <Icon name="manage_search" className="text-2xl text-green-400" />
            </div>
            <p className="mt-3 text-sm font-bold text-white">What You See</p>
            <p className="mt-0.5 text-xs text-slate-400">The result. Nothing more.</p>

            <div className="mt-5 space-y-2">
              {/* What passes through */}
              <p className="text-[10px] font-bold uppercase tracking-widest text-green-600/70">
                Passes through
              </p>
              <div className="flex items-center gap-2.5 rounded-lg border border-green-600/40 bg-green-900/30 px-3 py-2.5">
                <Icon name="verified" filled className="text-base text-green-400" />
                <span className="text-xs font-bold text-green-300">
                  YouAreYou Verified ✓
                </span>
              </div>

              {/* What's blocked */}
              <p className="pt-2 text-[10px] font-bold uppercase tracking-widest text-slate-600">
                Never passes through
              </p>
              {BLOCKED_ITEMS.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2.5 rounded-lg border border-slate-700/30 bg-slate-800/20 px-3 py-2 opacity-55"
                >
                  <Icon name="block" className="text-sm text-red-500/80" />
                  <span className="text-xs font-medium text-slate-500 line-through">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Bottom note */}
        <p className="mt-8 text-center text-xs text-slate-600">
          YouAreYou is a SOC 2 certified trusted third party.
          We hold the proof — you share only the badge.
        </p>

      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// WhatVerifiedMeans — 3 icon cards explaining the badge
// ---------------------------------------------------------------------------

const VERIFIED_PILLARS = [
  {
    icon: "badge",
    iconBg: "bg-blue-50",
    iconColor: "text-primary-500",
    title: "Government ID Checked",
    description:
      "They've submitted a government-issued photo ID — driver's license or passport — and it passed automated checks.",
  },
  {
    icon: "face",
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    title: "Liveness Confirmed",
    description:
      "A real-time liveness check confirmed they are a real, living person — not a photo, video, or AI-generated face.",
  },
  {
    icon: "verified_user",
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
    title: "Identity Locked In",
    description:
      "Their verified identity is stored on YouAreYou. You don't need to see their ID — we've already checked it.",
  },
];

function WhatVerifiedMeansSection() {
  return (
    <section className="bg-background-light py-12 sm:py-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="text-center">
          <h2 className="text-2xl font-extrabold tracking-tight text-text-primary sm:text-3xl">
            What "YouAreYou Verified" actually means
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-text-secondary sm:text-base">
            Not a self-reported profile. Not a social login. Bank-grade identity verification.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {VERIFIED_PILLARS.map((pillar) => (
            <div
              key={pillar.title}
              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${pillar.iconBg}`}>
                <Icon name={pillar.icon} filled className={`text-2xl ${pillar.iconColor}`} />
              </div>
              <h3 className="mt-4 text-sm font-bold text-text-primary">{pillar.title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-text-secondary">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-primary-100 bg-primary-50 px-4 py-3">
          <Icon name="shield" filled className="mt-0.5 shrink-0 text-lg text-primary-500" />
          <p className="text-xs text-text-secondary leading-relaxed">
            <span className="font-semibold text-text-primary">
              You never see their documents.
            </span>{" "}
            Neither party shares raw ID scans, SSNs, or biometric data. You simply receive
            a trusted confirmation that everything checks out — backed by the same infrastructure
            used by banks and financial institutions.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Use Cases — 3 scenario cards
// ---------------------------------------------------------------------------

const USE_CASES = [
  {
    icon: "storefront",
    title: "Marketplace & Classifieds",
    scenario: "Someone wants to buy your car off Craigslist. You have their phone number.",
    action: "Check their phone — know they're real before you share your address.",
  },
  {
    icon: "home",
    title: "Rentals & Housing",
    scenario: "A landlord wants first and last month's rent before you've met in person.",
    action: "Check their name and city — confirm they're a verified person before sending money.",
  },
  {
    icon: "favorite",
    title: "Dating & Social",
    scenario: "You matched with someone online and are planning a first meet-up.",
    action: "Check their email — get peace of mind that they are who their profile says.",
  },
];

function UseCasesSection() {
  return (
    <section className="bg-white py-12 sm:py-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="text-center">
          <h2 className="text-2xl font-extrabold tracking-tight text-text-primary sm:text-3xl">
            When does this matter?
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-text-secondary sm:text-base">
            Any time you're about to meet, pay, or trust someone you only know from the internet.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {USE_CASES.map((uc) => (
            <div
              key={uc.title}
              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100">
                <Icon name={uc.icon} className="text-2xl text-text-secondary" />
              </div>
              <h3 className="mt-4 text-sm font-bold text-text-primary">{uc.title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-text-muted">{uc.scenario}</p>
              <p className="mt-2 text-xs font-semibold leading-relaxed text-primary-600">
                {uc.action}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// FinalCTA
// ---------------------------------------------------------------------------

function FinalCtaSection() {
  return (
    <section className="bg-background-light py-12 sm:py-16">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 p-8 text-center text-white shadow-xl sm:p-10">
          <Icon name="verified" filled className="mx-auto mb-3 text-4xl text-white/80" />
          <h2 className="text-xl font-extrabold sm:text-2xl">
            Want to be found as verified?
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-white/80">
            Join YouAreYou for free. Get verified in under 2 minutes and share your
            identity with anyone who needs to know you're real.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-primary-600 shadow-sm transition-colors hover:bg-gray-50"
            >
              Get Verified — It's Free
              <Icon name="arrow_forward" className="text-base" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Hero — compact, search is the star
// ---------------------------------------------------------------------------

function HeroSection() {
  return (
    <section className="bg-gradient-to-b from-white to-background-light pb-2 pt-8 sm:pt-10">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 text-center">
        <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5">
          <Icon name="verified_user" filled className="text-base text-primary-500" />
          <span className="text-sm font-semibold text-primary-600">
            YouAreYou Identity Network
          </span>
        </div>

        <h1 className="text-4xl font-black leading-tight tracking-tight text-text-primary sm:text-5xl">
          Reality Check
        </h1>
        <p className="mt-3 text-lg font-semibold text-text-secondary sm:text-xl">
          Are they really who they say they are?
        </p>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-text-secondary">
          Search our network of verified identities before you meet, rent, buy, or hire.
          No account needed.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// RealityCheckPage — Main Component
// ---------------------------------------------------------------------------

export default function RealityCheckPage() {
  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <Header />

      <main className="flex-1">
        <HeroSection />
        <SearchPanel />
        <TrustDiagramSection />
        <WhatVerifiedMeansSection />
        <UseCasesSection />
        <FinalCtaSection />
      </main>

      <Footer />
    </div>
  );
}
